import type { ActivityResponse, JobRun } from "@/hooks/useActivity";
import { LEDGER_ROWS_ONLY_WHEN_STOPPED, RESUMABLE, RUN_AGAIN_PATH, STORE_OP, kindLabel } from "@/lib/jobRuns";
import type { SessionActivity } from "@/stores/activity-store";

/** One line in the Activity panel that isn't a live op from the bar. */
export interface ActivityRow {
  key: string;
  tone: "running" | "waiting" | "success" | "failed" | "cancelled";
  title: string;
  sub: string | null;
  /** "Now: …" line for running rows. */
  now?: string | null;
  /** When it finished (or last checked), for the time column. */
  at: number | null;
  /** Right-hand time text, when it isn't a plain clock time. */
  timeText?: string | null;
  action?: "showMonitorPapers" | "runAgain" | "resume";
  /** job_runs id, for runAgain / resume. */
  runId?: string;
}

const PROVIDER = (p: string) => (p === "mendeley" ? "Mendeley" : "Zotero");
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function clockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function ago(ms: number, now: number): string {
  const min = Math.round((now - ms) / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  return clockTime(ms);
}

const MONITOR_STAGE: Record<string, string> = {
  collecting: "collecting new papers from your seeds",
  deduplicating: "removing papers you already have",
  scoring: "scoring the new papers",
  normalizing: "finishing up",
};

/** Waiting = angle-rescore states that exist on the server but were never
 * named in the UI. Running / batch show as the bar's angle-rescore op. */
export function waitingRows(data: ActivityResponse | undefined): ActivityRow[] {
  const a = data?.angle_rescore;
  if (!a) return [];
  const at = a.not_before ? clockTime(Date.parse(a.not_before)) : null;
  const sub =
    a.state === "queued" ? "Starts within a minute (rescores run on a 1-minute schedule)"
    : a.state === "settling" ? `Waiting for your edits to settle${at ? ` · starts by ${at}` : ""}`
    : a.state === "deferred" ? "Waiting for the current scoring batch to finish"
    : a.state === "retrying"
      ? `Didn't finish (${a.failures} ${a.failures === 1 ? "try" : "tries"} so far)${at ? ` · trying again at ${at}` : ""}`
    : null;
  if (!sub) return [];
  return [{ key: "angle-rescore-waiting", tone: "waiting", title: "Rescoring angles", sub, at: null }];
}

/** Server-known work running now that the bar has no op for. `hasOp`
 * tells whether the bar already shows a live op of that type. */
export function serverRunningRows(
  data: ActivityResponse | undefined,
  hasOp: (type: string) => boolean,
): ActivityRow[] {
  if (!data) return [];
  const rows: ActivityRow[] = [];
  for (const j of data.running) {
    if (j.kind === "monitor") {
      if (hasOp("monitor")) continue;
      rows.push({
        key: "srv-monitor", tone: "running",
        title: `Living Lit check${j.automatic ? " (scheduled)" : ""}`,
        sub: null,
        now: j.detail ? `Now: ${MONITOR_STAGE[j.detail] ?? j.detail}` : null,
        at: Date.parse(j.started_at),
      });
    } else {
      if (hasOp("field-debate")) continue;
      const what = j.kind === "field_debate_refresh" ? "Field Debate refresh" : "Field Debate build";
      rows.push({
        key: `srv-${j.kind}`, tone: "running",
        title: `${what}${j.automatic ? " (automatic)" : ""}`,
        sub: null,
        now: j.detail ? `Now: ${j.detail}` : null,
        at: Date.parse(j.started_at),
      });
    }
  }
  if (data.abstract_backfill_running) {
    rows.push({
      key: "srv-abstracts", tone: "running", title: "Filling in abstracts (automatic)",
      sub: "For papers added without one", at: null,
    });
  }
  // A check covers every connected reference manager at once.
  const checking = data.sync_sources.filter((s) => s.checking);
  if (checking.length > 0) {
    rows.push({
      key: "srv-sync-checking", tone: "running",
      title: `Checking ${checking.map((s) => PROVIDER(s.provider)).join(" and ")}`,
      sub: "Looking for newly saved PDFs", at: null,
    });
  }
  return rows;
}

function finishedRow(f: ActivityResponse["finished"][number], i: number): ActivityRow {
  const at = Date.parse(f.finished_at);
  const tone = f.outcome === "success" ? "success" : f.outcome === "cancelled" ? "cancelled" : f.outcome === "skipped" ? "cancelled" : "failed";
  const failedSub = f.error ? `Failed: ${f.error}` : "Failed";
  const base = { key: `srv-done-${f.kind}-${f.finished_at}-${i}`, tone, at } as const;
  switch (f.kind) {
    case "monitor": {
      const n = f.count ?? 0;
      return {
        ...base, title: "Living Lit check",
        sub: f.outcome === "skipped"
          ? f.error === "no seed papers"
            ? "Skipped: no seed papers to follow"
            : "Skipped: earlier finds are still waiting in the inbox"
          : f.outcome !== "success" ? failedSub
          : n > 0 ? `Found ${plural(n, "new paper")} · scored` : "No new papers",
        action: f.outcome === "success" && n > 0 ? "showMonitorPapers" : undefined,
      };
    }
    case "field_debate_build":
    case "field_debate_refresh":
      return {
        ...base,
        title: f.kind === "field_debate_refresh" ? "Field Debate refresh" : "Field Debate build",
        sub: f.outcome === "success" ? (f.count ? `Map covers ${plural(f.count, "paper")}` : "Map updated")
          : f.outcome === "cancelled" ? "Cancelled" : failedSub,
      };
    case "scoring_batch":
      return {
        ...base, title: "Background scoring",
        sub: f.outcome === "success" ? `${plural(f.count ?? 0, "paper")} scored`
          : f.outcome === "cancelled" ? "Cancelled" : failedSub,
      };
    case "abstract_backfill":
      return {
        ...base, title: "Filled in abstracts (automatic)",
        sub: `${f.count ?? 0} fetched${f.failed_count ? ` · ${f.failed_count} not found` : ""}`,
      };
  }
}

function syncRow(s: ActivityResponse["sync_sources"][number], now: number): ActivityRow {
  const n = s.attached_today.length;
  const latest = s.attached_today[0];
  const checked = s.last_checked_at ? Date.parse(s.last_checked_at) : null;
  const first = latest ? `(“${latest.title.length > 40 ? `${latest.title.slice(0, 40)}…` : latest.title}”)` : "";
  return {
    key: `srv-sync-${s.provider}-done`,
    tone: s.status === "active" ? "success" : "failed",
    title: PROVIDER(s.provider),
    sub: s.status !== "active" ? "Needs reconnecting in Settings → Integrations"
      : n > 0 ? `Attached ${plural(n, "PDF")} ${n === 1 ? first : ""}`.trim() : "Nothing new attached today",
    at: latest ? Date.parse(latest.at) : checked,
    timeText: checked ? `checked ${ago(checked, now)}` : "not checked yet",
  };
}

function jobRunRow(run: JobRun, superseded: boolean): ActivityRow {
  const at = Date.parse(run.finished_at ?? run.created_at);
  const progress = run.total ? ` · ${run.done ?? 0} of ${run.total} done` : "";
  const base = { key: `run-${run.id}`, at, runId: run.id, title: kindLabel(run.kind) };
  switch (run.status) {
    case "stopped":
      return {
        ...base, tone: "cancelled",
        sub: `${run.error ?? "Stopped"}${progress}`,
        action: superseded ? undefined
          : RESUMABLE.has(run.kind) ? "resume" : RUN_AGAIN_PATH[run.kind] ? "runAgain" : undefined,
      };
    case "failed":
      return {
        ...base, tone: "failed", sub: run.error ? `Failed: ${run.error}` : "Failed",
        action: !superseded && RUN_AGAIN_PATH[run.kind] ? "runAgain" : undefined,
      };
    case "cancelled":
      return { ...base, tone: "cancelled", sub: `Cancelled${progress}` };
    default:
      return { ...base, tone: "success", sub: run.summary ?? "Finished" };
  }
}

/** Earlier today: finished jobs + server-recorded work + one line per
 * reference manager. Newest first. With durable jobs on, job_runs replaces
 * this tab's session list (it also has runs from other tabs and reloads). */
export function earlierRows(
  data: ActivityResponse | undefined,
  session: SessionActivity[],
  now: number,
  durable = false,
): ActivityRow[] {
  const jobs = durable ? data?.jobs : undefined;
  const sessionRow = (e: SessionActivity): ActivityRow => ({
    key: e.id,
    tone: e.outcome,
    title: e.label,
    sub: e.summary,
    at: e.finishedAt,
  });
  // A newer run of the same kind makes Run again / Resume on an older one moot.
  const newest = new Map<string, number>();
  for (const j of jobs ?? []) {
    newest.set(j.kind, Math.max(newest.get(j.kind) ?? 0, Date.parse(j.created_at)));
  }
  // Session jobs the ledger doesn't record (e.g. clustering) stay listed.
  const ledgerOps = new Set(Object.values(STORE_OP));
  const rows: ActivityRow[] = jobs
    ? [
        ...jobs
          .filter((j) => j.status !== "running" && j.status !== "waiting")
          .filter((j) => j.status === "stopped" || !LEDGER_ROWS_ONLY_WHEN_STOPPED.has(j.kind))
          .map((j) => jobRunRow(j, (newest.get(j.kind) ?? 0) > Date.parse(j.created_at))),
        ...session.filter((e) => !ledgerOps.has(e.type)).map(sessionRow),
      ]
    : session.map(sessionRow);
  data?.finished.forEach((f, i) => rows.push(finishedRow(f, i)));
  rows.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
  // Reference managers last: they're a standing status, not an event.
  return [...rows, ...(data?.sync_sources ?? []).map((s) => syncRow(s, now))];
}

/** Things that finished since the panel was last opened and that you didn't
 * watch happen: server-recorded work and PDFs a reference manager attached. */
export function unreadCount(data: ActivityResponse | undefined, seenAt: number | undefined): number {
  if (!data) return 0;
  const since = seenAt ?? 0;
  const finished = data.finished.filter((f) => Date.parse(f.finished_at) > since).length;
  const attached = data.sync_sources
    .flatMap((s) => s.attached_today)
    .filter((a) => Date.parse(a.at) > since).length;
  return finished + attached;
}
